import { VideoData } from '@lumen5/luminary';
import type { Scene, WatermarkPositions } from '@lumen5/luminary';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveSceneById, sceneDisplayId } from './scene-addressing';
import {
    applySceneWatermarkEdit,
    defaultSourceIndex,
    describeSources,
    validateSceneWatermarkEdit,
    validateSourceIndex,
} from './scene-watermark-edit';
import type { SceneWatermarkEdit } from './scene-watermark-edit';
import { VideoStateTool } from './VideoStateTool';

import { getIsUserAllowedAccessToWatermarks } from 'js/src/data/account';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    setWatermarkOpacityOperation,
    setWatermarkOperation,
    setWatermarkPositionOperation,
    setWatermarkVisibilityOperation,
} from 'js/src/luminary/luminary-operations';

export const setWatermarkSchema = z.object({
    scene_ids: z
        .array(z.string())
        .optional()
        .describe(
            'Scenes to change the watermark on, by scene_id from the video state. Omit to change the watermark for the whole video. Only narrows visibility, watermark_source_index and position; opacity is always video-wide.',
        ),
    visibility: z
        .boolean()
        .optional()
        .describe(
            'The visibility of the watermark. Provide this to change watermark visibility (show/hide).',
        ),
    watermark_source_index: z
        .int()
        .optional()
        .describe(
            'The index of the watermark to apply as watermark. Must be an index from the template watermark sources.',
        ),
    position: z
        .enum(VideoData.WATERMARK_POSITIONS)
        .optional()
        .describe(
            'The position where to place the watermark. Provide this to change watermark position.',
        ),
    opacity: z
        .number()
        .optional()
        .describe(
            'Watermark opacity, from 0 (invisible) to 100 (fully opaque). Always applies to the whole video.',
        ),
});
export type SetWatermarkArgs = z.infer<typeof setWatermarkSchema>;

export class SetWatermarkTool extends VideoStateTool<typeof setWatermarkSchema> {
    protected override readonly schema = setWatermarkSchema;
    protected override readonly toolName = TOOL_NAMES.setWatermark;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        "Set the video's watermark (the brand logo the template overlays on every scene): watermark_source_index changes its source, position moves it, visibility shows or hides it, and opacity fades it. Applies to the whole video unless scene_ids narrows it to specific scenes. " +
        'Only change it when the user asks about their watermark or logo.';

    public override getToolDisplayName(): string {
        return 'Setting watermark';
    }

    public get statusIcon(): string {
        return 'branding_watermark';
    }

    public override getSubagentAccess(args: Record<string, unknown>): SubagentAccess {
        // Without scene_ids the change lands on the whole video, as does opacity in any call.
        const namesScenes = Array.isArray(args.scene_ids) && args.scene_ids.length > 0;
        return namesScenes && args.opacity === undefined
            ? SubagentAccess.EDITS_TARGET
            : SubagentAccess.BLOCKED;
    }

    public async execute(args: SetWatermarkArgs, context: ToolExecutionContext) {
        const { watermark_source_index, position, visibility, opacity } = args;

        // check if the user has the watermark capability
        const canSetWatermark = SERVER?.USER?.capabilities?.custom_watermark ?? false;
        if (!canSetWatermark) {
            return `Error: Current plan does not support custom watermarks. If the user asked for this watermark change, use ${TOOL_NAMES.suggestUpgrade} to show them they need to upgrade. Otherwise leave the watermark as it is and do not mention it.`;
        }

        const edit: SceneWatermarkEdit = { visibility, watermark_source_index, position };
        const editsWatermark =
            visibility !== undefined || watermark_source_index !== undefined || !!position;
        if (!editsWatermark && opacity === undefined) {
            return 'Error: Please provide visibility, watermark_source_index, position or opacity.';
        }
        if (opacity !== undefined && (!Number.isFinite(opacity) || opacity < 0 || opacity > 100)) {
            return `Error: opacity must be between 0 and 100 (got ${opacity}).`;
        }

        // An empty scene_ids names no scenes, which is the same request as omitting it.
        const sceneIds = args.scene_ids?.length ? args.scene_ids : undefined;

        // The editor gates its video-wide watermark panel on this permission, but not the
        // per-scene watermark toolbar.
        const changesWholeVideo = opacity !== undefined || (editsWatermark && !sceneIds);
        if (changesWholeVideo && !getIsUserAllowedAccessToWatermarks(this.getState())) {
            return 'Error: This user does not have watermark access, so they cannot change the watermark for the whole video. A team admin grants watermark access in the team settings. Changes to specific scenes (with scene_ids) are still allowed.';
        }

        // Check everything before changing anything, so an invalid part leaves the video as it was.
        let scenes: Scene[] = [];
        if (editsWatermark) {
            if (sceneIds) {
                const resolved = this.resolveScenes(sceneIds, edit);
                if ('error' in resolved) {
                    return resolved.error;
                }
                scenes = resolved.scenes;
            } else {
                const error = this.validateVideoEdit(edit);
                if (error) {
                    return error;
                }
            }
        }

        const results: string[] = [];
        if (editsWatermark) {
            results.push(
                sceneIds
                    ? this.applyToScenes(scenes, edit, context.parentActionGroup)
                    : this.applyToVideo(edit, context.parentActionGroup),
            );
        }
        if (opacity !== undefined) {
            const { setWatermarkOpacity } = makeOperationsCallable(
                { setWatermarkOpacityOperation },
                this.luminary,
                this.dispatch,
                this.getState,
            );
            // The operation takes a percentage and divides by 100 itself.
            setWatermarkOpacity(opacity, false, context.parentActionGroup);
            results.push(`Success: Set the watermark opacity to ${opacity}% for the whole video.`);
        }
        return results.join(' ');
    }

    private resolveScenes(
        sceneIds: string[],
        edit: SceneWatermarkEdit,
    ): { scenes: Scene[] } | { error: string } {
        const scenes: Scene[] = [];
        for (const sceneId of sceneIds) {
            const resolved = resolveSceneById(this.luminary, this.getState(), sceneId);
            if ('error' in resolved) {
                return { error: `Error: ${resolved.error}` };
            }
            const error = validateSceneWatermarkEdit(
                this.luminary,
                resolved.scene,
                sceneDisplayId(this.luminary, resolved.scene.id),
                edit,
            );
            if (error) {
                return { error };
            }
            scenes.push(resolved.scene);
        }
        return { scenes };
    }

    private applyToScenes(
        scenes: Scene[],
        edit: SceneWatermarkEdit,
        parentActionGroup: string,
    ): string {
        const results = scenes.map((scene) =>
            applySceneWatermarkEdit(
                this.luminary,
                this.dispatch,
                scene,
                sceneDisplayId(this.luminary, scene.id),
                edit,
                parentActionGroup,
            ),
        );
        if (edit.visibility !== false && !this.luminary.template.watermark.visible) {
            // A scene's watermark renders only while the video's is on.
            results.push(
                'Note: the watermark is hidden for the whole video, so this shows nothing until it is turned on (call again with visibility: true and no scene_ids).',
            );
        }
        return results.join(' ');
    }

    private validateVideoEdit({
        visibility,
        watermark_source_index,
        position,
    }: SceneWatermarkEdit): string | null {
        const sourceError = validateSourceIndex(this.luminary, watermark_source_index);
        if (sourceError) {
            return sourceError;
        }
        const { availablePositions } = this.luminary.template.watermark;
        if (position && !availablePositions.includes(position as WatermarkPositions)) {
            return `Error: Invalid position "${position}". Available positions are: ${availablePositions.join(', ')}`;
        }
        if (
            visibility === true &&
            watermark_source_index === undefined &&
            this.videoSourceToShow() === null
        ) {
            return `Error: The video has no watermark chosen. Pass watermark_source_index to choose one: ${describeSources(this.luminary)}.`;
        }
        return null;
    }

    /**
     * What to show when turning the video's watermark on without naming a source: the one it
     * already has, else the template's only source, else null to ask.
     */
    private videoSourceToShow(): number | 'current' | null {
        if (typeof this.luminary.template.watermark.sourceData === 'object') {
            return 'current';
        }
        return defaultSourceIndex(this.luminary);
    }

    private applyToVideo(
        { visibility, watermark_source_index, position }: SceneWatermarkEdit,
        parentActionGroup: string,
    ): string {
        const { setWatermark, setWatermarkPosition, setWatermarkVisibility } =
            makeOperationsCallable(
                {
                    setWatermarkOperation,
                    setWatermarkPositionOperation,
                    setWatermarkVisibilityOperation,
                },
                this.luminary,
                this.dispatch,
                this.getState,
            );
        const hiding = visibility === false;
        const changes: string[] = [];
        const notes: string[] = [];

        if (watermark_source_index !== undefined) {
            if (hiding) {
                notes.push(
                    `watermark source index ${watermark_source_index} was not applied because the watermark is hidden`,
                );
            } else {
                // Choosing a source also turns the watermark on.
                setWatermark(watermark_source_index, false, parentActionGroup);
                changes.push(`source index ${watermark_source_index}`);
            }
        }
        if (position) {
            setWatermarkPosition(position as WatermarkPositions, false, parentActionGroup);
            changes.push(`position ${position}`);
        }
        if (hiding) {
            setWatermarkVisibility(false, false, parentActionGroup);
            changes.push('hidden');
        } else if (visibility === true && watermark_source_index === undefined) {
            const sourceToShow = this.videoSourceToShow();
            if (typeof sourceToShow === 'number') {
                setWatermark(sourceToShow, false, parentActionGroup);
                changes.push(`shown with source index ${sourceToShow}`);
            } else {
                setWatermarkVisibility(true, false, parentActionGroup);
                changes.push('shown');
            }
        }

        const summary = `Success: Updated the watermark for the whole video: ${changes.join(', ')}.`;
        return notes.length > 0 ? `${summary} Note: ${notes.join('; ')}.` : summary;
    }
}
